import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Compass,
  Sparkles,
  MapPin,
  CheckCircle2,
  Globe,
  Share2,
  Eye,
  Star,
  Users
} from "lucide-react";

export default function HallFinderPage({ businessInfo }) {
  const [isListed, setIsListed] = useState(true);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const hallName = businessInfo?.business_name || "Grand Royal Palace & Convention Hall";
  const city = businessInfo?.city || "Bengaluru";
  const capacity = businessInfo?.capacity_guests || 500;
  const dayRate = businessInfo?.pricing_day_slot || 45000;

  const handleToggleListing = () => {
    setIsListed(!isListed);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto" data-testid="hall-finder-page">
      {/* Top Header with Back Navigation */}
      <div className="flex items-center justify-between">
        <Link
          to="/calendar"
          data-testid="hall-finder-back-btn"
          className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-pink-900 hover:text-pink-950 bg-white px-4 py-2 rounded-xl border border-pink-200 shadow-sm transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Calendar</span>
        </Link>

        <span className="text-xs font-semibold text-gray-500">
          Hall Finder Directory Listing
        </span>
      </div>

      {/* Main Container Card */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-xl overflow-hidden">
        {/* Banner */}
        <div className="bg-gradient-to-r from-pink-900 via-pink-800 to-rose-800 p-6 sm:p-8 text-white">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-xs font-semibold mb-2">
            <Compass className="w-3.5 h-3.5 text-pink-200" />
            <span>Public Directory</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
            List on Hall Finder
          </h1>
          <p className="text-pink-100 text-xs sm:text-sm mt-1 max-w-xl">
            Publish your convention hall on the BookMyEvents public customer marketplace to receive direct customer inquiries and event leads.
          </p>
        </div>

        {/* Content Body */}
        <div className="p-6 sm:p-8 space-y-6 bg-gray-50/30">
          {showSuccessToast && (
            <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-2xl text-xs sm:text-sm text-emerald-900 flex items-center gap-2 font-bold animate-fade-in">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <span>Listing status updated successfully!</span>
            </div>
          )}

          {/* Listing Status Card */}
          <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-gray-900">Marketplace Status:</span>
                <span
                  data-testid="listing-status-badge"
                  className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${
                    isListed
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {isListed ? "Active & Visible" : "Unlisted / Hidden"}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                {isListed
                  ? "Your venue is actively listed on BookMyEvents Hall Finder."
                  : "Your venue is currently hidden from public search."}
              </p>
            </div>

            <button
              data-testid="toggle-listing-btn"
              onClick={handleToggleListing}
              className={`px-6 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all shadow-sm ${
                isListed
                  ? "bg-rose-50 border border-rose-300 text-rose-700 hover:bg-rose-100"
                  : "bg-pink-900 text-white hover:bg-pink-950"
              }`}
            >
              {isListed ? "Pause / Hide Listing" : "Publish to Hall Finder"}
            </button>
          </div>

          {/* Preview Card */}
          <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950 flex items-center gap-2 border-b border-pink-100 pb-3">
              <Eye className="w-4 h-4 text-pink-700" />
              <span>Customer Preview Card</span>
            </h3>

            <div className="max-w-md mx-auto bg-gradient-to-br from-pink-50/50 to-white rounded-2xl border border-pink-200 p-5 shadow-sm space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="inline-flex items-center gap-1 text-[11px] font-bold text-pink-800 bg-pink-100/80 px-2 py-0.5 rounded">
                    <Sparkles className="w-3 h-3 text-pink-700" />
                    <span>Verified Convention Hall</span>
                  </div>
                  <h4 className="text-base font-extrabold text-pink-950">{hallName}</h4>
                  <p className="text-xs text-gray-600 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-pink-600" />
                    <span>{city}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg text-amber-800 text-xs font-bold">
                  <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                  <span>4.9</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-pink-100 text-xs text-gray-700">
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-pink-700" />
                  <span>Capacity: {capacity} guests</span>
                </div>
                <div className="flex items-center gap-1.5 font-bold text-pink-950">
                  <span>Starting ₹{dayRate.toLocaleString("en-IN")}/slot</span>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-white rounded-2xl border border-pink-100 text-center space-y-2">
              <div className="w-10 h-10 rounded-xl bg-pink-50 text-pink-900 flex items-center justify-center mx-auto">
                <Globe className="w-5 h-5" />
              </div>
              <h5 className="text-xs font-bold text-pink-950">10x More Reach</h5>
              <p className="text-[11px] text-gray-500">Over 50,000 monthly event organizers browsing halls in your city.</p>
            </div>

            <div className="p-4 bg-white rounded-2xl border border-pink-100 text-center space-y-2">
              <div className="w-10 h-10 rounded-xl bg-pink-50 text-pink-900 flex items-center justify-center mx-auto">
                <Share2 className="w-5 h-5" />
              </div>
              <h5 className="text-xs font-bold text-pink-950">Direct Leads</h5>
              <p className="text-[11px] text-gray-500">Inquiries and booking requests synced directly into your portal.</p>
            </div>

            <div className="p-4 bg-white rounded-2xl border border-pink-100 text-center space-y-2">
              <div className="w-10 h-10 rounded-xl bg-pink-50 text-pink-900 flex items-center justify-center mx-auto">
                <Sparkles className="w-5 h-5" />
              </div>
              <h5 className="text-xs font-bold text-pink-950">Zero Commission</h5>
              <p className="text-[11px] text-gray-500">Keep 100% of your earnings with direct owner-to-customer communication.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
